import { search } from "algolia-instant-search";
import {
  AlgoliaFilterBuilder,
  RT_PRODUCT_FEED_FILTERS,
} from "@theme/algolia-filter-builder";
import { VirtualWidget } from "@theme/algolia-virtual-widget";
const { stats, configure } = instantsearch.widgets;
const { connectSearchBox } = instantsearch.connectors;
const virtualSearchBox = connectSearchBox(() => {});

// Saved vehicle is the single source of truth for fitment (localStorage,
// written by the vehicle selectors). Returns its display string ("2004 BMW
// 330i") or null — used to frame the /search title as a vehicle landing page.
// Requires `gid` (not just `display`): the facet clause keys on gid, so a
// gid-less stale vehicle applies NO filter — gating on gid keeps the title
// ("Parts for your …") honest, never claiming a vehicle the results aren't
// actually filtered to.
const savedVehicleDisplay = () => {
  const v = window.FyreFitment.getVehicle();
  return v && v.gid && v.display ? v.display : null;
};

if (!customElements.get("main-search")) {
  class MainSearch extends HTMLElement {
    isB2B = this.dataset.b2b === "true";
    filterClauses = new AlgoliaFilterBuilder({
      status: "ACTIVE",
      excludeBuilderBacking: { clause: 'NOT tags:"builder-backing"' },
    });

    connectedCallback() {
      if (this.isB2B) {
        this.filterClauses.set("rtProductFeed", RT_PRODUCT_FEED_FILTERS.b2b);
      } else {
        this.filterClauses.set("rtProductFeed", RT_PRODUCT_FEED_FILTERS.retail);
      }

      search.addWidgets([
        virtualSearchBox({}),
        stats({
          container: "#search-page-title",
          templates: {
            // Two ways to reach /search: the fitment form (a vehicle is saved —
            // this is a VEHICLE LANDING page for a no-VPP vehicle) or a raw text
            // query. Make the path obvious in the title. The result count lives
            // in #stats, not here.
            text({ nbHits, query }) {
              const vehicle = savedVehicleDisplay();
              if (nbHits === 0) {
                if (query && vehicle)
                  return `No results for "${query}" on your ${vehicle}`;
                if (query) return `No results for "${query}"`;
                if (vehicle) return `No parts found for your ${vehicle}`;
                return "No results";
              }
              if (query && vehicle) return `"${query}" for your ${vehicle}`;
              if (query) return `Search: "${query}"`;
              if (vehicle) return `Parts for your ${vehicle}`;
              return "Search";
            },
          },
        }),
        stats({
          container: "#stats",
          templates: {
            text({ nbHits, page, hitsPerPage }) {
              if (nbHits === 0) return "No results";
              const first = page * hitsPerPage + 1;
              const last = Math.min((page + 1) * hitsPerPage, nbHits);
              return `${nbHits.toLocaleString()} results (showing ${first}–${last})`;
            },
          },
        }),
        configure({
          distinct: 1,
          maxValuesPerFacet: 100,
          facetingAfterDistinct: true,
        }),
        this.fitmentVehicleWidget,
      ]);

      this.bindFiltersToggle();
      this.bindSidebarBreakpointFix();

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.startSearch());
      } else {
        this.startSearch();
      }
    }

    // Start InstantSearch, then re-assert the saved vehicle's fitment facet.
    // WHY: a no-VPP vehicle picked in the fitment form full-page-navigates to a
    // bare /search, where the facet must apply from localStorage. The router's
    // first-query seed of `fitment` can miss on a cold load, so results fail open
    // to the whole catalog (the "3,269 for 2004 BMW 330i" bug). Re-firing the
    // known-good `fitment:changed` path the moment the instance starts forces the
    // clause deterministically (at worst one extra query that snaps 3,269 → the
    // filtered count). No-op when no vehicle is saved (raw search is unaffected)
    // or when a stale saved vehicle has no gid (the gid gate above).
    // No-vehicle facet cap. On a cold /search with NO division picked and
    // NO fitment clause applied, the mixed-division list is a ~59-facet wall. Collapse it to the
    // first N visible facets (DOM order = D085 priority). Recompute from scratch every render so
    // un-cap is free the instant a scope lands. Hides via the `.facet-capped` class + !important
    // (see the filter snippet's inline <style>): auto-hide owns inline `display`, this owns the
    // class — the two never touch each other's channel (a display write here would be resurrected
    // by auto-hide's per-render rewrite, or stick and never restore).
    applyFacetCap() {
      const CAP = 6;
      this.querySelectorAll(".facet-capped").forEach((n) =>
        n.classList.remove("facet-capped"),
      );
      // Predicate keys on the APPLIED state, never the stored vehicle: a stale gid-less vehicle
      // applies no clause → the results ARE the wall → the cap must stay on.
      const ui = Object.values(search.getUiState() || {})[0] || {};
      const rl = ui.refinementList || {};
      // The Product-Line selector auto-hides when the results carry a single division (<=1 value) —
      // i.e. the results are ALREADY composition-scoped. Treat that as scoped, or a single-division
      // text query (the common case) would cap with the selector — its own escape
      // hatch — gone. display:none is auto-hide's inline write, settled by the time
      // this instance-level render handler runs.
      const selectorHost = this.querySelector(
        'algolia-refinement-list[data-attribute="metafield_specs_type"]',
      );
      const scoped =
        (rl.metafield_specs_type || []).length > 0 ||
        (selectorHost && selectorHost.style.display === "none") ||
        (
          (search.helper &&
            search.helper.state &&
            search.helper.state.filters) ||
          ""
        ).includes("metafield_fitment_vehicles:");
      if (scoped) return; // a division / single-division composition / a vehicle scopes → auto-hide governs
      let kept = 0;
      this.querySelectorAll("algolia-refinement-list").forEach((host) => {
        if (host.closest(".filters__more-group")) return; // More-group hosts handled by the group
        if (host.dataset.attribute === "metafield_specs_type") return; // never cap the scoping control
        if (host.style.display === "none") return; // auto-hidden this render → not counted
        if ((rl[host.dataset.attribute] || []).length > 0) return; // exempt an actively-refined facet
        if (kept < CAP) {
          kept++;
          return;
        }
        host.classList.add("facet-capped");
      });
      // Cap the "More filters" group (exactly one, parent-owned, shared by auto + moto) UNLESS it
      // holds an actively-refined facet — hiding a group with a live nested refinement would conceal
      // a filter still constraining results while only the current-refinements pill reflects it.
      this.querySelectorAll("details.filters__more-group").forEach((g) => {
        const hasRefined = Array.prototype.some.call(
          g.querySelectorAll("algolia-refinement-list"),
          (h) => (rl[h.dataset.attribute] || []).length > 0,
        );
        if (!hasRefined) g.classList.add("facet-capped");
      });
    }

    startSearch() {
      // Reveal the (Liquid-hidden) page title once the stats widget has rendered
      // into it, so an empty margined <h1> never flashes before content lands.
      search.on("render", () => {
        const el = document.getElementById("search-page-title");
        if (el && el.textContent.trim() && el.parentElement) {
          el.parentElement.style.display = "";
        }
        this.applyFacetCap();
      });
      search.start();
      const saved = window.FyreFitment.getVehicle();
      if (saved && saved.gid) {
        // Re-fire the module's own contract: `fitment:changed` on window, detail =
        // the vehicle. getVehicle() already returns null when storage is blocked.
        window.dispatchEvent(
          new CustomEvent("fitment:changed", { detail: saved }),
        );
      }
    }

    get fitmentVehicleWidget() {
      return new VirtualWidget({
        $$type: "algolia.fitment_vehicle_filter",
        uiStateKey: "fitment",
        buildSearchParameters: (searchParameters, fitment) => {
          // One encoding, one owner: FyreFitment.vehicleFilter is the only code that
          // turns a vehicle into an Algolia clause, and it carries the moto-only
          // universal allowlist and the base36 gid encoding the index holds. It
          // returns "" for a gid-less vehicle, which is exactly when no clause may
          // apply — a gid-less vehicle must never silently filter to the whole catalog
          // while the page claims a vehicle. The universal flag preserves the clause
          // main has emitted live since 39a19e2.
          const clause = window.FyreFitment.vehicleFilter(fitment, { universal: true });
          if (clause) this.filterClauses.set("fitment", clause);
          else this.filterClauses.delete("fitment");

          return searchParameters.setQueryParameter(
            "filters",
            this.filterClauses.toString(),
          );
        },
      });
    }

    bindFiltersToggle() {
      const toggle = this.querySelector("#FiltersToggle");
      const overlay = this.querySelector("#FiltersOverlay");
      const sidebar = this.querySelector("#collection__sidebar");
      if (!toggle || !sidebar) return;

      const openFilters = () => {
        sidebar.classList.add("is-active");
        if (overlay) overlay.classList.add("is-active");
        toggle.setAttribute("aria-expanded", "true");
        document.body.style.overflow = "hidden";
      };

      const closeFilters = () => {
        sidebar.classList.remove("is-active");
        if (overlay) overlay.classList.remove("is-active");
        toggle.setAttribute("aria-expanded", "false");
        document.body.style.overflow = "";
      };

      toggle.addEventListener("click", openFilters);
      if (overlay) overlay.addEventListener("click", closeFilters);

      document.addEventListener("click", (e) => {
        if (e.target.closest('[data-action="close-filters"]')) closeFilters();
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeFilters();
      });

      this.closeFilters = closeFilters;
    }

    // The sidebar switches from in-flow layout to a fixed off-canvas drawer
    // at 991px. Crossing that breakpoint mid-resize otherwise animates the
    // drawer's transform transition, making it visibly slide across the
    // screen. Briefly hiding it kills that stray transition.
    bindSidebarBreakpointFix() {
      const sidebar = this.querySelector("#collection__sidebar");
      if (!sidebar) return;

      const mql = window.matchMedia("(max-width: 991px)");
      mql.addEventListener("change", (e) => {
        // The drawer's "is-active" state (and the body scroll lock it sets)
        // is mobile-only. Without this, resizing to desktop while the drawer
        // is open leaves body scroll locked and the sidebar stuck "active".
        if (!e.matches) this.closeFilters?.();

        sidebar.classList.add("no-transition");
        sidebar.style.display = "none";
        requestAnimationFrame(() => {
          sidebar.style.display = "";
          requestAnimationFrame(() => {
            sidebar.classList.remove("no-transition");
          });
        });
      });
    }
  }

  customElements.define("main-search", MainSearch);
}
